import {
    getBuyerActiveItemsLabel,
    getBuyerItemsLostLabel,
    getActiveLabel,
    getSoldLabel,
    getUnsoldLabel,
    getPurchasesLabel
} from './item-labels';
import { getActionsForItem } from './item-actions';

import { getColumns } from './item-columns';
import {
    buyerActiveItemsColumns,
    buyerPurchasesColumns,
    buyerItemsLostColumns,
    sellerActiveColumns,
    sellerSoldColumns,
    sellerUnsoldColumns
} from './../../constants/column-definitions';
import { logger } from 'packages/logger';
import { PAGE_NAMES } from '../../constants/page';

/**
 * @param {boolean} declares if response is incorrect;
 */
const hasIncorrectResponse = (response) =>
    !response ||
    response === null ||
    response === undefined ||
    typeof response === 'string';

/**
 * @param {object} response - copy of the response with empty values;
 */
const createReturnObject = (response) => ({
    paging: response.paging,
    itemsOrderedByID: [],
    itemsByID: {},
    statistics: response.statistics,
    currentTime: response.currentTime,
    currentMemberId: response.currentMemberId,
    sellerEmail: response.sellerEmail || ''
});

/**
 * TODO
 * Describe the relationship of transactionID / PurchaseID for future developers;
 *
 * @param {object} item - the item from webApi
 * @returns {object} The single item transformed with all properties set as expected for the front end.
 * @desc - the purchase
 */
const getItem = (
    item,
    labelFunc,
    columnFunc,
    listType,
    listViewType,
    memberId,
    memberMessagingParticipation,
    pageName
) => ({
    ...item,
    key: item.transaction ? item.transaction.transactionId : item.itemId,
    bidInformation: {
        ...item.bidInformation,
        hasBids: item.bidInformation.bidCount > 0
    },
    label: labelFunc(item, memberId),
    actions: getActionsForItem(
        item,
        listViewType,
        memberId,
        memberMessagingParticipation,
        pageName
    ),
    columns: columnFunc(item, listType),
    shippingPrice: getCheapestShippingPrice(item),
    leadingBidder:
        item.bidInformation.leadingBidder && item.isActive
            ? item.bidInformation.leadingBidder
            : null,
    buyer:
        item.transaction && item.transaction.buyer
            ? item.transaction.buyer
            : null,
    itemUrl:
        item.itemUrl.substring(0, 1) === '/' ? item.itemUrl : `/${item.itemUrl}`
});

const getCheapestShippingPrice = (item) => {
    const shippingOptions = (item.listing.shippingOptions || [])
        .filter(
            (option) =>
                option.shippingType !== 'Takeaway' &&
                option.shippingType !== 'TakeawayOnly'
        )
        .map(({ shippingCost }) => shippingCost);

    return shippingOptions.length > 0
        ? shippingOptions.reduce((prev, current) =>
              prev < current ? prev : current
          )
        : 0;
};

/**
 * @returns {Object} : Defines which labels and actions are used for each page
 */
const actionLabelMap = {
    [PAGE_NAMES.BuyerActiveItems]: {
        label: getBuyerActiveItemsLabel,
        columns: buyerActiveItemsColumns,
        pageName: PAGE_NAMES.BuyerActiveItems
    },
    [PAGE_NAMES.BuyerPurchases]: {
        label: getPurchasesLabel,
        columns: buyerPurchasesColumns,
        pageName: PAGE_NAMES.BuyerPurchases
    },
    [PAGE_NAMES.BuyerItemsLost]: {
        label: getBuyerItemsLostLabel,
        columns: buyerItemsLostColumns,
        pageName: PAGE_NAMES.BuyerItemsLost
    },
    [PAGE_NAMES.SellerActive]: {
        label: getActiveLabel,
        columns: sellerActiveColumns,
        pageName: PAGE_NAMES.SellerActive
    },
    [PAGE_NAMES.SellerSold]: {
        label: getSoldLabel,
        columns: sellerSoldColumns,
        pageName: PAGE_NAMES.SellerSold
    },
    [PAGE_NAMES.SellerUnsold]: {
        label: getUnsoldLabel,
        columns: sellerUnsoldColumns,
        pageName: PAGE_NAMES.SellerUnsold
    }
};

const getSoldItem = (item, listViewType, messagingParticipation = false) =>
    getItem(
        item,
        getSoldLabel,
        getColumns(sellerSoldColumns[listViewType], listViewType),
        actionLabelMap[PAGE_NAMES.SellerSold].columns[listViewType],
        listViewType,
        '',
        messagingParticipation,
        PAGE_NAMES.SellerSold
    );
const getUnsoldItem = (item, listViewType) =>
    getItem(
        item,
        getUnsoldLabel,
        getColumns(sellerUnsoldColumns[listViewType], listViewType),
        actionLabelMap[PAGE_NAMES.SellerUnsold].columns[listViewType],
        listViewType,
        '',
        false,
        PAGE_NAMES.SellerUnsold
    );
const getPurchasedItem = (item, listViewType) =>
    getItem(
        item,
        getPurchasesLabel,
        getColumns(buyerPurchasesColumns[listViewType], listViewType),
        actionLabelMap[PAGE_NAMES.BuyerPurchases].columns[listViewType],
        listViewType,
        '',
        true,
        PAGE_NAMES.BuyerPurchases
    );

/**
 * @param {object} response - from webApi
 * @param {string} listViewType
 * @param {string} memberId
 * @param {boolean} memberMessagingParticipation
 * @param {string} pageName
 * @description creates an ordered list of items, with ui state set for frontend;
 */
const getItemsInOrder = (
    response,
    listViewType,
    memberId,
    memberMessagingParticipation,
    pageName
) => {
    if (!actionLabelMap[pageName]) {
        try {
            throw actionLabelMap[pageName];
        } catch (e) {
            logger(e);
        }
    }
    const newItems = createReturnObject(response);
    if (hasIncorrectResponse(response)) {
        return newItems;
    }

    Object.keys(response.items).forEach((key) => {
        const item = response.items[key];
        let id = 1;
        switch (pageName) {
            case PAGE_NAMES.BuyerActiveItems:
            case PAGE_NAMES.BuyerItemsLost:
            case PAGE_NAMES.SellerUnsold:
            case PAGE_NAMES.SellerActive:
                id = item.itemId.toString();
                break;
            case PAGE_NAMES.BuyerPurchases:
            case PAGE_NAMES.SellerSold:
                id = item.transaction.transactionId.toString();
                break;
            default:
                try {
                    throw `trying to update state on undefined (${pageName}) pageName`;
                } catch (e) {
                    logger(e);
                }
        }
        newItems.itemsOrderedByID.push(id);
        // eslint-disable-next-line better-mutation/no-mutation
        newItems.itemsByID[id] = getItem(
            item, //item,
            actionLabelMap[pageName].label, //labelFunc,
            getColumns(
                actionLabelMap[pageName].columns[listViewType],
                listViewType
            ), //columnFunc,
            actionLabelMap[pageName].columns[listViewType], //listType,
            listViewType, //listViewType,
            memberId, //memberId,
            memberMessagingParticipation, // memberMessagingParticipation
            actionLabelMap[pageName].pageName //pageName
        );
    });

    return newItems;
};

export const getItemFromPurchaseOrderId = (items, purchaseOrderId) => {
    const { itemsByID } = items;
    const item = Object.keys(itemsByID).find((key) => {
        const item = itemsByID[key];
        return item.transaction.purchaseOrderId === purchaseOrderId;
    });

    return item ? itemsByID[item] : null;
};

export default {
    getSoldItem,
    getUnsoldItem,
    getItemsInOrder,
    getPurchasedItem,
    getItemFromPurchaseOrderId
};
